import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { type Href } from 'expo-router';
import { Animated } from 'react-native';

import { Alert, AlertText } from '@/components/ui/alert';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { REFERRAL_COPY } from '@/constants/referrals';
import type { ReferralLookupResult } from '@/services/getReferralByCodeV2';

export type CreateAccountReferralBannerProps = {
  isVisible: boolean;
  referralLookupData: ReferralLookupResult | null | undefined;
  referralBannerOpacity: Animated.Value;
  referralBannerTranslateY: Animated.Value;
  onPressReferrerProfile: (href: Href) => void;
};

export function CreateAccountReferralBanner({
  isVisible,
  referralLookupData,
  referralBannerOpacity,
  referralBannerTranslateY,
  onPressReferrerProfile,
}: CreateAccountReferralBannerProps) {
  if (!isVisible) {
    return null;
  }

  const referrerDisplayName = referralLookupData?.referrerDisplayName;

  if (!referrerDisplayName) {
    return null;
  }

  const textSize = isWeb ? 'lg' : 'sm';

  const descriptionSize = isWeb ? 'md' : 'sm';

  let referrerNameContent = (
    <Text size={textSize} className="font-bold text-info-800">
      {referrerDisplayName}
    </Text>
  );

  if (referralLookupData?.referrerId) {
    referrerNameContent = (
      <LinkText
        size={textSize}
        className="font-bold text-info-800 underline data-[hover=true]:text-info-900"
        onPress={() =>
          onPressReferrerProfile(`/(open)/profile/${String(referralLookupData.referrerId)}` as Href)
        }>
        {referrerDisplayName}
      </LinkText>
    );
  }

  return (
    <Animated.View
      style={{
        opacity: referralBannerOpacity,
        transform: [{ translateY: referralBannerTranslateY }],
      }}>
      <Alert
        action="info"
        className="mb-5 w-full flex-col items-start gap-0 border border-info-300 px-3 py-2.5 shadow-sm web:mb-7 web:px-5 web:py-4">
        <VStack className="w-full flex-1 gap-1">
          <AlertText size={textSize} className="w-full text-typography-900">
            <Text size={textSize} className="font-semibold text-typography-900">
              You were referred by{' '}
            </Text>
            {referrerNameContent}
            <Text size={textSize} className="font-semibold text-typography-900">
              !
            </Text>
          </AlertText>
          <AlertText size={descriptionSize} className="w-full text-typography-900 web:leading-snug">
            {REFERRAL_COPY.invitee.createAccountBannerCopy}
          </AlertText>
        </VStack>
      </Alert>
    </Animated.View>
  );
}
